import React, { useEffect } from 'react';

interface GoogleTagManagerProps {
  id: string;
}

// Detect react-snap's puppeteer UA so analytics aren't injected into the
// prerendered HTML (would otherwise double-fire when the page hydrates).
const IS_PRERENDERING =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const GTM_SRC_PREFIX = 'https://www.googletagmanager.com/gtm.js';

/**
 * Loads Google Tag Manager imperatively and defers the (render-blocking)
 * gtm.js download off the critical path. The `dataLayer` is initialized and
 * the `gtm.start` event is pushed synchronously on mount so any events queued
 * before injection are preserved, but the actual `<script>` is only injected
 * on the first of: requestIdleCallback, the first user interaction, or a 3s
 * timeout fallback.
 */
export const GoogleTagManager: React.FC<GoogleTagManagerProps> = ({ id }) => {
  useEffect(() => {
    // Don't execute if ID is empty or invalid, or during prerender.
    if (!id || id.trim() === '' || IS_PRERENDERING) {
      return;
    }

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    // Initialize the dataLayer and push gtm.start immediately so queued
    // events aren't lost while we wait to inject the script.
    const w = window as unknown as {
      dataLayer?: Array<Record<string, unknown>>;
      requestIdleCallback?: (
        _cb: () => void,
        _opts?: { timeout: number }
      ) => number;
    };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    let injected = false;
    const interactionEvents = [
      'pointerdown',
      'keydown',
      'touchstart',
      'scroll',
    ] as const;

    const cleanupListeners = () => {
      interactionEvents.forEach(evt => {
        window.removeEventListener(evt, inject);
      });
    };

    function inject() {
      if (injected) return;
      injected = true;
      cleanupListeners();

      try {
        // Guard against double-injection.
        if (document.querySelector(`script[src^="${GTM_SRC_PREFIX}"]`)) {
          return;
        }

        const script = document.createElement('script');
        script.async = true;
        script.src = `${GTM_SRC_PREFIX}?id=${id}`;
        script.onerror = () => {
          console.warn('GTM script failed to load');
        };
        document.head.appendChild(script);
      } catch (e) {
        console.warn('GTM initialization failed:', e);
      }
    }

    // Defer injection: idle callback (with timeout), first user interaction,
    // or a hard 3s fallback — whichever fires first.
    let idleHandle: number | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      idleHandle = w.requestIdleCallback(inject, { timeout: 3000 });
    }

    interactionEvents.forEach(evt => {
      window.addEventListener(evt, inject, { once: true, passive: true });
    });

    const timeoutId = window.setTimeout(inject, 3000);

    return () => {
      cleanupListeners();
      window.clearTimeout(timeoutId);
      if (
        idleHandle !== undefined &&
        typeof (
          window as unknown as { cancelIdleCallback?: (_h: number) => void }
        ).cancelIdleCallback === 'function'
      ) {
        (
          window as unknown as { cancelIdleCallback: (_h: number) => void }
        ).cancelIdleCallback(idleHandle);
      }
    };
  }, [id]);

  return null;
};

export const GoogleTagManagerNoScript: React.FC<GoogleTagManagerProps> = ({
  id,
}) => {
  useEffect(() => {
    // Don't execute if ID is empty or invalid, or during prerender.
    if (!id || id.trim() === '' || IS_PRERENDERING) {
      return;
    }

    try {
      // Create the noscript element
      const noscriptElement = document.createElement('noscript');

      // Create the iframe element
      const iframeElement = document.createElement('iframe');
      iframeElement.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
      iframeElement.height = '0';
      iframeElement.width = '0';
      iframeElement.style.display = 'none';
      iframeElement.style.visibility = 'hidden';
      iframeElement.title = 'Google Tag Manager';

      // Append the iframe to the noscript element
      noscriptElement.appendChild(iframeElement);

      // Insert the noscript element at the beginning of the body
      if (document.body && document.body.firstChild) {
        document.body.insertBefore(noscriptElement, document.body.firstChild);
      }

      // Cleanup function to remove the element when component unmounts
      return () => {
        try {
          if (document.body && document.body.contains(noscriptElement)) {
            document.body.removeChild(noscriptElement);
          }
        } catch (e) {
          console.warn('GTM noscript cleanup failed:', e);
        }
      };
    } catch (e) {
      console.warn('GTM noscript initialization failed:', e);
    }
  }, [id]);

  return null;
};

export default GoogleTagManager;
